import { Card, CardContent, CardHeader, Container, Grid, styled, Typography } from '@material-ui/core'
import { FC, useEffect, useState } from 'react'
import { generatePath, useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import { ButtonClose } from '../component/web/project/page/pageAction'
import useTranslate from '../i18n/useTranslate'
import { RoutePath } from '../router/routesConfig'
import useVerifyDocument from '../services/web/useVerifyDocument'
import { convertDateToFormatEN, convertDateToFormatTH } from '../utilities/date'
import { decryptionAES, encryptionAES } from '../utilities/encryption'

type MatchParams = {
    projectData: string
}

const CustomHeader = styled(CardHeader)({
    borderBottom: '1px solid #DCDCDC',
})

const CustomUnderlineGrid = styled(Grid)({
    borderBottom: '1px solid #DCDCDC',
})

type VerifiedInfo = {
    docStatus: { [key: string]: string }
    updatedDate: { [key: string]: string }
    verifyDocRemark: string
    uniqueId: number
    statusColor: string
    rowNo: string
}

const DocumentVerifyHistory: FC<{}> = () => {
    const { state } = useLocation()
    const history = useHistory()
    const matchPath = useRouteMatch<MatchParams>(RoutePath.documentVerifyHistory)
    const encryptedProjectData = matchPath ? matchPath.params.projectData : null
    const projectData = encryptedProjectData ? JSON.parse(decryptionAES(encryptedProjectData)) : null
    const { getVerifyHistory } = useVerifyDocument()
    const { projectID, submittedID, submittedTypeID } = projectData
    const translate = useTranslate()
    const [verifiedInfo, setVerifiedInfo] = useState<VerifiedInfo[]>([])
    const [projectInfo, setProjectInfo] = useState<{ [key: string]: any }>({
        th: {
            researcherName: '',
            projectName: '',
            projectType: '',
        },
        en: {
            researcherName: '',
            projectName: '',
            projectType: '',
        },
    })

    const getDocumentVerifyHistory = async () => {
        const response = await getVerifyHistory(submittedID, submittedTypeID)
        response && initialData(response?.result)
    }

    const initialData = (data: any) => {
        if (data) {
            const firstRow = data.projectInfo
            setProjectInfo({
                th: {
                    researcherName: firstRow.researcherNameTH ?? '-',
                    projectName: firstRow.projectNameTH ?? '-',
                    projectType: firstRow.projectTypeTH ?? '-',
                },
                en: {
                    researcherName: firstRow.researcherNameEN ?? '-',
                    projectName: firstRow.projectNameEN ?? '-',
                    projectType: firstRow.projectTypeEN ?? '-',
                },
            })
            const formatVerifiedData = data?.historyList?.map((verifiedData: any) => {
                return {
                    docStatus: {
                        th: verifiedData.verify_doc_name_th,
                        en: verifiedData.verify_doc_name_en,
                    },
                    updatedDate: {
                        th: verifiedData.history_updated_date
                            ? convertDateToFormatTH(verifiedData.history_updated_date)
                            : '-',
                        en: verifiedData.history_updated_date
                            ? convertDateToFormatEN(verifiedData.history_updated_date)
                            : '-',
                    },
                    verifyDocRemark: verifiedData.verify_doc_remark,
                    uniqueId: verifiedData.history_id,
                    statusColor: verifiedData.verify_doc_color,
                    rowNo: verifiedData.row_no ?? '-',
                }
            })
            setVerifiedInfo(formatVerifiedData)
        }
    }

    const handleClose = () => {
        const encryptedProjectID = encryptionAES(projectID)
        const encryptedSubmittedID = encryptionAES(submittedID)

        const path = generatePath(RoutePath.manageProjectEdit, {
            projectID: encryptedProjectID,
            submittedID: encryptedSubmittedID,
        })

        history.push({
            pathname: path,
            state: state,
        })
    }

    useEffect(() => {
        getDocumentVerifyHistory()
    }, [])

    return (
        <Container maxWidth="lg">
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Card>
                        <CustomHeader
                            title={
                                <Typography variant="h5">
                                    {translate.get('LABEL.DOCUMENT_VERIFICATION_HISTORY')}
                                </Typography>
                            }
                        />
                        <CardContent style={{ padding: '24px' }}>
                            <Grid container spacing={2}>
                                <Grid item xs={true}>
                                    <Typography variant="body2">
                                        {translate.get('LABEL.RESEARCHER_FL_NAME')} :{' '}
                                        {projectInfo[translate.languageStore.currentLanguage].researcherName ?? '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={12} lg={5} xl={4}>
                                    <Typography variant="body2">
                                        {translate.get('LABEL.PROJECT_TYPE')} :{' '}
                                        {projectInfo[translate.languageStore.currentLanguage].projectType ?? '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body2">
                                        {translate.get('LABEL.PROJECT_NAME')} :{' '}
                                        {projectInfo[translate.languageStore.currentLanguage].projectName ?? '-'}
                                    </Typography>
                                </Grid>
                            </Grid>
                            {verifiedInfo.map((data) => (
                                <Grid container key={data.uniqueId} spacing={2}>
                                    <CustomUnderlineGrid item xs={12}>
                                        &nbsp;
                                    </CustomUnderlineGrid>
                                    <Grid item xs={12}>
                                        <pre />
                                        <Typography variant="h5">
                                            {translate.get('LABEL.DOCUMENT_VERIFICATION_RESULT_TIMES', [data.rowNo])}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={12} lg={6}>
                                        <Typography variant="body2">
                                            {translate.get('LABEL.STATUS')} :{' '}
                                            <span style={{ color: data.statusColor, fontWeight: 'bolder' }}>
                                                {data.docStatus[translate.languageStore.currentLanguage] ?? '-'}
                                            </span>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={12} lg={6}>
                                        <Typography variant="body2">
                                            {translate.get('LABEL.SPECIFIED_REASON_DATE')} :{' '}
                                            {data.updatedDate[translate.languageStore.currentLanguage] ?? '-'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="body2">
                                            {translate.get('LABEL.REASON')} : {data.verifyDocRemark ?? '-'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            ))}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12}>
                    <Grid container justify="flex-end">
                        <ButtonClose onClickClose={handleClose}></ButtonClose>
                    </Grid>
                </Grid>
            </Grid>
        </Container>
    )
}

export default DocumentVerifyHistory
